<template>
  <div class="service-quantity-wrapper">
    <!-- Multiselect with Quantity Inline -->
    <v-select
      :model-value="selectedServices"
      :items="options"
      item-title="text"
      item-value="value"
      :label="label"
      :rules="rules"
      :readonly="readonly"
      :bg-color="readonly ? 'grey-lighten-4' : (modified ? 'amber-lighten-5' : undefined)"
      :class="{ 'modified-field': modified }"
      multiple
      outlined
      dense
      density="compact"
      @update:modelValue="onServicesChanged"
    >
      <template v-if="modified" v-slot:append-inner>
        <v-icon size="x-small" color="amber-darken-2" title="Modificado">mdi-pencil-circle</v-icon>
      </template>
      
      <!-- Custom chip template to show quantity inline -->
      <template v-slot:selection="{ item, index }">
        <v-chip
          :key="index"
          size="small"
          closable
          @click:close="removeService(item.value)"
          class="selection-chip"
        >
          {{ item.title }}
          
          <!-- Quantity Badge with Menu -->
          <strong 
            class="ml-1 quantity-badge"
            @click.stop
            @mousedown.stop
          >
            {{ getQuantity(item.value) }}
            
            <v-menu
              activator="parent"
              :close-on-content-click="false"
              location="bottom"
            >
              <v-card width="200" class="pa-3">
                <div class="text-body-2 font-weight-medium mb-2">
                  {{ getServiceLabel(item.value) }}
                </div>
                <v-text-field
                  :model-value="getQuantity(item.value)"
                  @update:modelValue="setQuantity(item.value, $event)"
                  type="number"
                  min="0"
                  label="Quantidade"
                  outlined
                  dense
                  density="compact"
                  hide-details
                  autofocus
                  @click.stop
                  @keydown.stop
                ></v-text-field>
              </v-card>
            </v-menu>
          </strong>
        </v-chip>
      </template>
    </v-select>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    default: () => []
  },
  label: {
    type: String,
    default: 'Serviços'
  },
  readonly: {
    type: Boolean,
    default: false
  },
  rules: {
    type: Array,
    default: () => []
  },
  modified: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

// Extract selected services from modelValue
const selectedServices = computed(() => {
  const model = props.modelValue || []
  return model.map(item => item.service || item)
})

// Get quantity for a service
const getQuantity = (service) => {
  const model = props.modelValue || []
  const found = model.find(item => item.service === service || item === service)
  if (!found) return 1
  return found.quantity || 1
}

// Set quantity for a service
const setQuantity = (service, quantity) => {
  const numQuantity = parseInt(quantity) || 0
  const model = props.modelValue || []
  
  // Find or create entry
  let found = false
  const updated = model.map(item => {
    if (item.service === service || item === service) {
      found = true
      return { service, quantity: numQuantity }
    }
    return item
  })
  
  if (!found && numQuantity > 0) {
    updated.push({ service, quantity: numQuantity })
  }
  
  emit('update:modelValue', updated)
}

// Handle services selection change
const onServicesChanged = (newServices) => {
  const model = props.modelValue || []
  
  // Remove services that are no longer selected
  let updated = model.filter(item => {
    const svc = item.service || item
    return newServices.includes(svc)
  })
  
  // Add new services with default quantity 1
  newServices.forEach(service => {
    if (!updated.find(item => item.service === service || item === service)) {
      updated.push({ service, quantity: 1 })
    }
  })
  
  emit('update:modelValue', updated)
}

const removeService = (service) => {
  const model = props.modelValue || []
  const updated = model.filter(item => (item.service || item) !== service)
  emit('update:modelValue', updated)
}

const getServiceLabel = (serviceValue) => {
  const service = props.options.find(opt => opt.value === serviceValue)
  return service ? service.text : serviceValue
}
</script>

<style scoped>
.service-quantity-wrapper {
  width: 100%;
}

.selection-chip {
  display: inline-flex !important;
  align-items: center;
  margin: 2px 4px 2px 0 !important;
  flex-shrink: 0;
  cursor: pointer;
}

.ml-1 {
  margin-left: 0.25rem;
}

.quantity-badge {
  cursor: pointer;
  padding: 0 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
  font-size: 1.3em;
  color: #1976D2;
  font-weight: 900;
}

.quantity-badge:hover {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>




